import BaseApi from "src/store/BaseApi";
import { Theme } from "./client.type";
import type {
  FlightTransaction,
  FlightTransactionsRequest,
} from "src/Modules/UserHistory/pages/OrdersList/components/TransactionPages/FlightsTransactions/constants";
import type {
  HotelTransaction,
  HotelTransactionsRequest,
} from "src/Modules/UserHistory/pages/OrdersList/components/TransactionPages/HotelsTransactions/constants";
import type { UserOrderItem, UserOrdersRequest, UserOrdersResponse } from "src/Modules/UserHistory/pages/OrdersList/constants";

export type AirportRef = {
  code: string;
  name: string;
  terminal: string;
  cityName: string;
  countryName: string;
};

export type Flight = {
  flightNumber: string;
  airline: string;
  airlineCode: string;
  origin: AirportRef;
  destination: AirportRef;
  departure: string;
  arrival: string;
  durationMinutes: number;
  cabin: string;
  checkedBaggage: string;
  carryOn: string;
};

export type Layover = {
  airport: string;
  airportName: string;
  city: string;
  durationMinutes: number;
};

export type Segment = {
  direction: "Outbound" | "Inbound";
  origin: string;
  destination: string;
  departure: string;
  arrival: string;
  durationMinutes: number;
  stops: number;
  flights: Flight[];
  layovers: Layover[];
};

export type IdDetail = {
  paxId: number;
  idType: number;
  idNumber: string;
  issuedCountryCode: string;
  issueDate: string;
  expiryDate: string;
};

export type SeatAddOn = {
  airlineCode: string;
  flightNumber: string;
  craftType: string;
  origin: string;
  destination: string;
  availabilityType: string;
  availablityType: number;
  description: number;
  code: string;
  rowNo: string;
  seatNo: string;
  seatType: number;
  seatSubType: string;
  seatWayType: number;
  compartment: number;
  deck: number;
  currency: string;
  price: number;
};

export type MealAddOn = {
  airlineCode: string;
  flightNumber: string;
  wayType: string;
  code: string;
  description: string;
  airlineDescription: string;
  quantity: number;
  currency: string;
  price: number;
  origin: string;
  destination: string;
};

export type BaggageAddOn = {
  airlineCode: string;
  flightNumber: string;
  wayType: string;
  code: string;
  description: string;
  weight: number;
  currency: string;
  price: number;
  origin: string;
  destination: string;
  text: string | null;
};

export type Passenger = {
  paxId: number;
  title: string;
  firstName: string;
  lastName: string;
  type: "Adult" | "Child" | "Infant";
  gender: string;
  email: string;
  mobile1: string;
  mobile1CountryCode: string;
  isLeadPax: boolean;
  dateOfBirth: string;
  nationality: string | null;
  country: { countryCode: string; countryName: string | null };
  city: { countryCode: string | null; cityCode: string; cityName: string };
  addressLine1: string;
  addressLine2: string | null;
  passportNo: string;
  passportExpiry: string;
  passportIssueDate: string;
  passengerIdNo: string;
  passengerIdType: number;
  passengerIdExpiry: string;
  passengerIdIssueDate: string;
  passengerIdIssueCountryCode: string;
  idCardNo: string | null;
  idDetails: IdDetail[];
  ffAirline: string | null;
  ffNumber: string | null;
  seat: SeatAddOn[] | null;
  meal: MealAddOn[] | null;
  baggage: BaggageAddOn[] | null;
  frequentFlyerNumber: string | null;
  ticketNumber: string | null;
};

export type FlightOrderFareRule = {
  origin: string;
  destination: string;
  airline: string;
  fareBasisCode: string;
  fareRestriction: string | null;
  ruleDetail: string;
  inclusions: string | null;
};

export type FlightOrderDetailsRequest = {
  referenceNumber?: string;
};

export type FlightOrderDetails = {
  orderId: string;
  pnr: string | null;
  externalProviderReference: string | null;
  bookingCreationDate: string;
  status: number;
  airlineName: string;
  origin: string;
  destination: string;
  nonRefundable: boolean;
  hasLayovers: boolean;
  totalAmount: number;
  totalPointsRedeemed: number;
  cashAmountPaid: number | null;
  segments: Segment[];
  passengers: Passenger[];
  fareRules: FlightOrderFareRule[];
  fareSummary: unknown | null;
  transactions: unknown[];
};

const PrivateAPIWithTags = BaseApi.enhanceEndpoints({
  addTagTypes: ["userDetails", "userFlights", "userHotels", "userOrders"],
});



export const ClientApi = PrivateAPIWithTags.injectEndpoints({
  endpoints: (builder) => ({
    getModules: builder.query<any, any>({
      query: () => ({
        url: `/Module`,
        method: "GET",
      }),
      transformResponse: (response: any) => response?.result,
      extraOptions: {
        notifyOnFailure: true,
      },
    }),
    getUserDetails: builder.query<any, any>({
      query: () => ({
        url: `/User/GetUserDetails`,
        method: "GET",
      }),
      providesTags: ["userDetails"],
      transformResponse: (response: any) => response?.result,
      extraOptions: {
        notifyOnFailure: true,
      },
    }),
    getUserFlights: builder.query<FlightTransaction[], FlightTransactionsRequest>({
      query: (body) => ({
        url: `/travel/Flights/OrderList`,
        method: "POST",
        body,
      }),
      providesTags: ["userFlights"],
      transformResponse: (response: { result: FlightTransaction[] }) => response?.result,
      extraOptions: {
        notifyOnFailure: true,
      },
    }),
    getUserHotels: builder.query<HotelTransaction[], HotelTransactionsRequest>({
      query: (body) => ({
        url: `/hotels/orders`,
        method: "POST",
        body,
      }),
      providesTags: ["userHotels"],
      transformResponse: (response: { result: HotelTransaction[] }) => response?.result,
      extraOptions: {
        notifyOnFailure: true,
      },
    }),
    getUserFlightDetails: builder.query<FlightOrderDetails, FlightOrderDetailsRequest>({
      query: (body) => ({
        url: `/travel/Flights/OrderDetail`,
        method: "POST",
        body,
      }),
      providesTags: ["userFlights"],
      transformResponse: (response: { result: FlightOrderDetails }) => response?.result,
      extraOptions: {
        notifyOnFailure: true,
      },
    }),
    getUserOrders: builder.query<UserOrdersResponse<UserOrderItem>, UserOrdersRequest>({
      query: (body) => ({
        url: `/Order/GetAllOrders`,
        method: "POST",
        body,
      }),
      providesTags: ["userOrders"],
      transformResponse: (response: { result: UserOrdersResponse<UserOrderItem> }) => response?.result,
      extraOptions: {
        notifyOnFailure: true,
      },
    }),
    getUserOrderDetails: builder.query<any, any>({
      query: (body) => ({
        url: `/Order/GetOrderDetails`,
        method: "POST",
        body,
      }),
      transformResponse: (response: any) => response?.result,
      extraOptions: {
        notifyOnFailure: true,
      },
    }),
    getClientTheme: builder.query<Theme, undefined>({
      query: () => ({
        url: `/RewardCatalogConfig/GetBrandingColors`,
        method: "GET",
      }),
      transformResponse: (response: {result:Theme}) => response?.result,
      extraOptions: {
        notifyOnFailure: true,
      },
    }),
  }),
});

type ClientApiEndpoints = keyof typeof ClientApi.endpoints;

export const {
  useGetUserOrdersQuery,
  useGetUserOrderDetailsQuery,
  useGetModulesQuery,
  useGetUserDetailsQuery,
  useGetUserFlightsQuery,
  useGetUserHotelsQuery,
  useGetUserFlightDetailsQuery,
  useGetClientThemeQuery,
} = ClientApi;
export { type ClientApiEndpoints };
